import { useEffect, useMemo, useState } from 'react';
import { Database, Heart, RotateCcw, Search, Undo2, X } from 'lucide-react';
import { draftReducer, initialDraft, loadDraft, saveDraft, type DraftAction } from './lib/draft';
import { inferFormation, type FormationResult } from './lib/formation';
import { recommend } from './lib/recommend';
import { rankHeroesByQuery } from './lib/search';
import { loadSnapshot } from './lib/snapshot';
import type { DataSnapshot, DraftMode, DraftState, DraftZone, Hero, Position, PrimaryAttribute, RankTier, Recommendation } from './types';

const tierLabels: Record<RankTier, string> = {
  all: '全部段位', herald: '先锋', guardian: '卫士', crusader: '中军', archon: '统帅', legend: '传奇', ancient: '万古流芳', divine: '超凡入圣', immortal: '冠世一绝',
};
const tierBadge: Partial<Record<RankTier, string>> = {
  herald: '/assets/ranks/rank_icon_1.png', guardian: '/assets/ranks/rank_icon_2.png', crusader: '/assets/ranks/rank_icon_3.png', archon: '/assets/ranks/rank_icon_4.png', legend: '/assets/ranks/rank_icon_5.png', ancient: '/assets/ranks/rank_icon_6.png', divine: '/assets/ranks/rank_icon_7.png', immortal: '/assets/ranks/rank_icon_8.png',
};
const attrLabels: Record<PrimaryAttribute | 'all', string> = { all: '全属性', strength: '力量', agility: '敏捷', intelligence: '智力', universal: '全才' };
const zoneLabels: Record<DraftZone, string> = { ally: '己方', enemy: '敌方', ban: '禁用' };
type RoleFilter = 'all' | 'core' | 'support';
type RecommendationKind = 'pick' | 'ban';

interface UrlViewState {
  draft: Partial<Pick<DraftState, 'mode' | 'tier' | 'targetPosition' | 'poolOnly'>>;
  query: string;
  attribute: PrimaryAttribute | 'all';
  role: RoleFilter;
  position: Position | 'all';
  recommendation: RecommendationKind;
}

const modes = new Set<DraftMode>(['ranked', 'league']);
const tiers = new Set<RankTier>(Object.keys(tierLabels) as RankTier[]);
const attributes = new Set<PrimaryAttribute | 'all'>(Object.keys(attrLabels) as Array<PrimaryAttribute | 'all'>);
const roles = new Set<RoleFilter>(['all', 'core', 'support']);
const scoreFormatter = new Intl.NumberFormat(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const rankFormatter = new Intl.NumberFormat(undefined, { minimumIntegerDigits: 2, useGrouping: false });
const integerFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

function readUrlViewState(): UrlViewState {
  const fallback: UrlViewState = { draft: {}, query: '', attribute: 'all', role: 'all', position: 'all', recommendation: 'pick' };
  if (typeof window === 'undefined') return fallback;

  const params = new URL(window.location.href).searchParams;
  const mode = params.get('mode') as DraftMode | null;
  const tier = params.get('tier') as RankTier | null;
  const targetPosition = Number(params.get('target')) as Position;
  const pool = params.get('pool');
  const attribute = params.get('attribute') as PrimaryAttribute | 'all' | null;
  const role = params.get('role') as RoleFilter | null;
  const position = params.get('position');
  const recommendation = params.get('recommendation');

  return {
    draft: {
      ...(mode && modes.has(mode) ? { mode } : {}),
      ...(tier && tiers.has(tier) ? { tier } : {}),
      ...([1, 2, 3, 4, 5].includes(targetPosition) ? { targetPosition } : {}),
      ...(pool === 'personal' || pool === 'all' ? { poolOnly: pool === 'personal' } : {}),
    },
    query: params.get('q') ?? '',
    attribute: attribute && attributes.has(attribute) ? attribute : 'all',
    role: role && roles.has(role) ? role : 'all',
    position: position && [1, 2, 3, 4, 5].includes(Number(position)) ? Number(position) as Position : 'all',
    recommendation: recommendation === 'ban' ? 'ban' : 'pick',
  };
}

function HeroImage({ hero, size = 'normal', priority = false }: { hero: Hero; size?: 'normal' | 'small'; priority?: boolean }) {
  return <img className={`hero-image hero-image--${size}`} src={hero.image} alt="" loading={priority ? 'eager' : 'lazy'} fetchPriority={priority ? 'high' : 'auto'} width={size === 'small' ? 42 : 72} height={size === 'small' ? 42 : 40} />;
}

function DraftStrip({ title, ids, heroes, zone, active, formation, overrides, onActivate, onRemove, onPositionChange }: {
  title: string;
  ids: number[];
  heroes: Map<number, Hero>;
  zone: DraftZone;
  active: boolean;
  formation?: FormationResult;
  overrides?: Record<string, Position>;
  onActivate: () => void;
  onRemove: (id: number) => void;
  onPositionChange?: (id: number, position: Position | null) => void;
}) {
  const slots = zone === 'ban' ? Math.max(7, ids.length) : 5;
  return (
    <section className={`draft-strip draft-strip--${zone} ${active ? 'is-active' : ''}`} aria-label={title}>
      <button className="strip-title" onClick={onActivate} aria-pressed={active}>
        <span>{title}</span><span className="strip-count">{ids.length}/{zone === 'ban' ? 14 : 5}</span>
      </button>
      <div className="draft-slots">
        {Array.from({ length: slots }, (_, index) => {
          const id = ids[index]; const hero = id ? heroes.get(id) : undefined;
          const prediction = formation?.predictions.find((item) => item.heroId === hero?.id);
          const confidenceClass = prediction ? (prediction.confidence < 55 ? 'is-low' : prediction.confidence < 75 ? 'is-medium' : 'is-high') : '';
          const occupied = new Set(Object.entries(overrides ?? {}).filter(([heroId]) => Number(heroId) !== hero?.id).map(([, position]) => position));
          return hero ? (
            <div key={hero.id} className="draft-slot is-filled">
              <HeroImage hero={hero} size="small" />
              <span className="draft-hero-info"><strong>{hero.nameZh}</strong>{zone !== 'ban' && prediction && <select
                className={`position-control ${confidenceClass}`}
                aria-label={`${hero.nameZh}位置`}
                value={overrides?.[String(hero.id)] ?? 'auto'}
                title={`候选概率：${Object.entries(prediction.probabilities).map(([p, probability]) => `P${p} ${Math.round(probability)}%`).join(' · ')}`}
                onChange={(event) => onPositionChange?.(hero.id, event.target.value === 'auto' ? null : Number(event.target.value) as Position)}
              >
                <option value="auto">{prediction.confidence < 55 ? '⚠ ' : ''}P{prediction.position}·{Math.round(prediction.confidence)}% 自动</option>
                {[1, 2, 3, 4, 5].map((position) => <option key={position} value={position} disabled={occupied.has(position as Position)}>P{position}·手动</option>)}
              </select>}</span>
              <button className="remove-hero" onClick={() => onRemove(hero.id)} title={`移除 ${hero.nameZh}`} aria-label={`移除 ${hero.nameZh}`}><X size={13} aria-hidden="true" /></button>
            </div>
          ) : <button key={`empty-${index}`} className="draft-slot" onClick={onActivate} aria-label={`激活${title}，添加英雄`}><span>{index + 1}</span></button>;
        })}
      </div>
    </section>
  );
}

function ScoreBar({ label, value }: { label: string; value: number }) {
  return <div className="score-row"><span>{label}</span><div className="score-track" aria-hidden="true"><i style={{ width: `${value}%` }} /></div><strong>{integerFormatter.format(value)}</strong></div>;
}

function RecommendationCard({ item, rank, onPick, relationshipSource, priority = false }: { item: Recommendation; rank: number; onPick: () => void; relationshipSource: string; priority?: boolean }) {
  return (
    <article className="recommendation-card">
      <button className="recommendation-main" onClick={onPick} aria-label={`${item.kind === 'ban' ? '禁用' : '选择'} ${item.hero.nameZh}`}>
        <span className="rank-index">{rankFormatter.format(rank)}</span>
        <HeroImage hero={item.hero} size="small" priority={priority} />
        <span className="recommendation-name"><strong>{item.hero.nameZh}</strong><small>{item.hero.nameEn}</small></span>
        <span className="total-score">{scoreFormatter.format(item.score)}</span>
      </button>
      <div className="score-grid">
        <ScoreBar label="对线" value={item.breakdown.lane} />
        <ScoreBar label="META" value={item.breakdown.meta} />
        <ScoreBar label="克制" value={item.breakdown.counter} />
        {item.kind === 'pick' ? <><ScoreBar label="协同" value={item.breakdown.synergy} /><ScoreBar label="位置" value={item.breakdown.position} /></> : <ScoreBar label="灵活" value={item.breakdown.flexibility} />}
      </div>
      <div className="lane-line"><span className="lane-tag">对位推断</span><span>{item.laneAnalysis.explanation}</span><small>置信 {item.laneAnalysis.confidence}% · 覆盖 {item.laneAnalysis.coverage}% · {item.laneAnalysis.source === 'laneMatchups' ? '真实对线' : item.laneAnalysis.source === 'mixed' ? `真实对线 + ${relationshipSource}` : item.laneAnalysis.source === 'matchups' ? `${relationshipSource}回退` : '中性值'}</small></div>
      <div className="reason-line"><span>{item.reasons.join(' / ')}</span><small>覆盖 {integerFormatter.format(item.coverage)}%</small></div>
    </article>
  );
}

export function App() {
  const [initialUrlView] = useState(readUrlViewState);
  const [snapshot, setSnapshot] = useState<DataSnapshot | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [state, setState] = useState<DraftState>(() => ({
    ...loadDraft(typeof window === 'undefined' ? undefined : window.localStorage),
    ...initialUrlView.draft,
  }));
  const [history, setHistory] = useState<DraftState[]>([]);
  const [query, setQuery] = useState(initialUrlView.query);
  const [attribute, setAttribute] = useState<PrimaryAttribute | 'all'>(initialUrlView.attribute);
  const [role, setRole] = useState<RoleFilter>(initialUrlView.role);
  const [positionFilter, setPositionFilter] = useState<Position | 'all'>(initialUrlView.position);
  const [recommendationKind, setRecommendationKind] = useState<RecommendationKind>(state.mode === 'league' ? initialUrlView.recommendation : 'pick');

  useEffect(() => {
    let active = true;
    setLoadError('');
    loadSnapshot()
      .then((nextSnapshot) => { if (active) setSnapshot(nextSnapshot); })
      .catch((error: Error) => { if (active) setLoadError(error.message); });
    return () => { active = false; };
  }, [loadAttempt]);
  useEffect(() => { saveDraft(window.localStorage, state); }, [state]);
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('mode', state.mode);
    url.searchParams.set('tier', state.tier);
    url.searchParams.set('target', String(state.targetPosition));
    url.searchParams.set('pool', state.poolOnly ? 'personal' : 'all');
    query ? url.searchParams.set('q', query) : url.searchParams.delete('q');
    attribute === 'all' ? url.searchParams.delete('attribute') : url.searchParams.set('attribute', attribute);
    role === 'all' ? url.searchParams.delete('role') : url.searchParams.set('role', role);
    positionFilter === 'all' ? url.searchParams.delete('position') : url.searchParams.set('position', String(positionFilter));
    recommendationKind === 'pick' ? url.searchParams.delete('recommendation') : url.searchParams.set('recommendation', recommendationKind);
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (nextUrl !== currentUrl) window.history.replaceState(window.history.state, '', nextUrl);
  }, [state.mode, state.tier, state.targetPosition, state.poolOnly, query, attribute, role, positionFilter, recommendationKind]);
  useEffect(() => {
    const restoreUrlState = () => {
      const next = readUrlViewState();
      const mode = next.draft.mode ?? initialDraft.mode;
      setState((current) => ({ ...current, ...next.draft }));
      setQuery(next.query);
      setAttribute(next.attribute);
      setRole(next.role);
      setPositionFilter(next.position);
      setRecommendationKind(mode === 'league' ? next.recommendation : 'pick');
    };
    window.addEventListener('popstate', restoreUrlState);
    return () => window.removeEventListener('popstate', restoreUrlState);
  }, []);

  const apply = (action: DraftAction, record = true) => {
    setState((current) => {
      const next = draftReducer(current, action);
      if (record && next !== current) setHistory((items) => [...items.slice(-39), current]);
      return next;
    });
  };
  const undo = () => setHistory((items) => {
    const prior = items.at(-1);
    if (prior) setState(prior);
    return prior ? items.slice(0, -1) : items;
  });

  const heroMap = useMemo(() => new Map(snapshot?.heroes.map((hero) => [hero.id, hero]) ?? []), [snapshot]);
  const allyFormation = useMemo(() => inferFormation(state.allies.map((id) => heroMap.get(id)).filter((hero): hero is Hero => Boolean(hero)), state.positionOverrides.ally), [state.allies, state.positionOverrides.ally, heroMap]);
  const enemyFormation = useMemo(() => inferFormation(state.enemies.map((id) => heroMap.get(id)).filter((hero): hero is Hero => Boolean(hero)), state.positionOverrides.enemy), [state.enemies, state.positionOverrides.enemy, heroMap]);
  const filteredHeroes = useMemo(() => {
    const heroes = (snapshot?.heroes ?? []).filter((hero) => {
      const matchesAttr = attribute === 'all' || hero.primaryAttribute === attribute;
      const matchesRole = role === 'all' || (role === 'core' ? hero.positions.some((p) => p <= 3) : hero.positions.some((p) => p >= 4));
      const matchesPosition = positionFilter === 'all' || hero.positions.includes(positionFilter);
      return matchesAttr && matchesRole && matchesPosition;
    });
    return rankHeroesByQuery(heroes, query);
  }, [snapshot, query, attribute, role, positionFilter]);
  const recommendations = useMemo(() => snapshot ? recommend(snapshot, state, recommendationKind) : [], [snapshot, state, recommendationKind]);
  const generatedAt = useMemo(() => snapshot ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short', hour12: false }).format(new Date(snapshot.metadata.generatedAt)) : '', [snapshot]);
  const selected = new Set([...state.allies, ...state.enemies, ...state.bans]);

  if (loadError) return <main className="fatal-state" role="alert"><Database size={36} aria-hidden="true" /><h1>数据快照加载失败</h1><p>{loadError}</p><p>检查网络连接，然后重新加载数据。</p><button className="retry-button" onClick={() => { setSnapshot(null); setLoadAttempt((attempt) => attempt + 1); }}>重新加载数据</button></main>;
  if (!snapshot) return <main className="fatal-state" role="status" aria-live="polite"><div className="loading-mark" aria-hidden="true" /><h1>载入 BP 数据…</h1></main>;

  const resetDraft = () => apply({ type: 'reset-draft' });
  const activeDataset = snapshot.metadata.datasets[state.mode];
  const datasetPatch = activeDataset.patch ? `补丁 ${activeDataset.patch}` : activeDataset.window;
  const datasetStatus = activeDataset.status === 'stale' ? '数据滞后' : activeDataset.status === 'cached' ? '缓存数据' : activeDataset.scope === 'rolling' ? '滚动更新' : '已更新';
  const relationshipSource = activeDataset.source.includes('Valve') ? 'Valve Dota Plus 整体克制' : state.mode === 'league' ? '联赛整体克制' : '整体克制';

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">跳至主要内容</a>
      <header className="topbar">
        <div className="product-mark"><span className="mark-shape" translate="no">BP</span><div><strong translate="no">DOTA 2 BP 实验台</strong><small>可解释阵容推荐</small></div></div>
        <div className="mode-cluster">
          <div className="mode-switch" role="group" aria-label="数据模式">
            <button className={state.mode === 'ranked' ? 'is-active' : ''} aria-pressed={state.mode === 'ranked'} onClick={() => { apply({ type: 'set', patch: { mode: 'ranked' } }); setRecommendationKind('pick'); }}>野排</button>
            <button className={state.mode === 'league' ? 'is-active' : ''} aria-pressed={state.mode === 'league'} onClick={() => { apply({ type: 'set', patch: { mode: 'league' } }); setRecommendationKind('pick'); }}>联赛</button>
          </div>
          <span className={`dataset-badge is-${activeDataset.status}`} role="status">{datasetPatch} · {datasetStatus}</span>
        </div>
        <div className="top-controls">
          {state.mode === 'ranked' && <label>段位{tierBadge[state.tier] && <img className="rank-badge" src={tierBadge[state.tier]} alt="" width="30" height="30" />}<select name="rank-tier" value={state.tier} onChange={(event) => apply({ type: 'set', patch: { tier: event.target.value as RankTier } })}>{Object.entries(tierLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>}
          <label>目标位置<select name="target-position" value={state.targetPosition} onChange={(event) => apply({ type: 'set', patch: { targetPosition: Number(event.target.value) as Position } })}>{[1, 2, 3, 4, 5].map((p) => <option key={p} value={p}>{p} 号位</option>)}</select></label>
          <button className="icon-button" onClick={undo} disabled={!history.length} aria-label="撤销"><Undo2 size={17} aria-hidden="true" /></button>
          <button className="icon-button" onClick={resetDraft} aria-label="重置草稿"><RotateCcw size={17} aria-hidden="true" /></button>
        </div>
      </header>

      <main className="workspace" id="main-content">
        <div className="draft-board">
          <DraftStrip title="己方阵容" ids={state.allies} heroes={heroMap} zone="ally" active={state.activeZone === 'ally'} formation={allyFormation} overrides={state.positionOverrides.ally} onActivate={() => apply({ type: 'set', patch: { activeZone: 'ally' } }, false)} onRemove={(heroId) => apply({ type: 'remove', heroId })} onPositionChange={(heroId, position) => apply({ type: 'set-position', side: 'ally', heroId, position })} />
          <DraftStrip title="敌方阵容" ids={state.enemies} heroes={heroMap} zone="enemy" active={state.activeZone === 'enemy'} formation={enemyFormation} overrides={state.positionOverrides.enemy} onActivate={() => apply({ type: 'set', patch: { activeZone: 'enemy' } }, false)} onRemove={(heroId) => apply({ type: 'remove', heroId })} onPositionChange={(heroId, position) => apply({ type: 'set-position', side: 'enemy', heroId, position })} />
          <DraftStrip title="已禁英雄" ids={state.bans} heroes={heroMap} zone="ban" active={state.activeZone === 'ban'} onActivate={() => apply({ type: 'set', patch: { activeZone: 'ban' } }, false)} onRemove={(heroId) => apply({ type: 'remove', heroId })} />
        </div>

        <section className="atlas-panel">
          <div className="panel-heading"><div><span className="eyebrow">HERO ATLAS</span><h1>英雄图鉴</h1></div><span className="active-zone">点击加入 · {zoneLabels[state.activeZone]}</span></div>
          <div className="filter-toolbar">
            <label className="search-box"><Search size={16} aria-hidden="true" /><input type="search" name="hero-search" autoComplete="off" spellCheck={false} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索中文、English 或缩写，如 SK…" aria-label="搜索英雄" /></label>
            <select name="attribute-filter" aria-label="属性筛选" value={attribute} onChange={(event) => setAttribute(event.target.value as PrimaryAttribute | 'all')}>{Object.entries(attrLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select>
            <select name="role-filter" aria-label="职责筛选" value={role} onChange={(event) => setRole(event.target.value as typeof role)}><option value="all">全职责</option><option value="core">核心</option><option value="support">辅助</option></select>
            <select name="position-filter" aria-label="位置筛选" value={positionFilter} onChange={(event) => setPositionFilter(event.target.value === 'all' ? 'all' : Number(event.target.value) as Position)}><option value="all">全位置</option>{[1, 2, 3, 4, 5].map((p) => <option key={p} value={p}>{p} 号位</option>)}</select>
            <button className={`pool-filter ${state.poolOnly ? 'is-active' : ''}`} aria-pressed={state.poolOnly} onClick={() => apply({ type: 'set', patch: { poolOnly: !state.poolOnly } }, false)}><Heart size={15} fill={state.poolOnly ? 'currentColor' : 'none'} aria-hidden="true" />{state.poolOnly ? '仅个人池' : '全部英雄'}</button>
          </div>
          <p className="sr-only" role="status" aria-live="polite">找到 {integerFormatter.format(filteredHeroes.length)} 位英雄</p>
          <div className="hero-grid">
            {filteredHeroes.map((hero, index) => {
              const inPool = state.personalPool.includes(hero.id);
              return <div key={hero.id} className={`hero-tile ${selected.has(hero.id) ? 'is-disabled' : ''}`}>
                <button className="hero-pick" onClick={() => apply({ type: 'add', heroId: hero.id })} disabled={selected.has(hero.id)} title={`加入${zoneLabels[state.activeZone]}`}>
                  <HeroImage hero={hero} priority={index === 0} /><span><strong>{hero.nameZh}</strong><small>{hero.nameEn}</small></span><em>{hero.positions.join('·')}</em>
                </button>
                <button className={`pool-toggle ${inPool ? 'is-active' : ''}`} onClick={() => apply({ type: 'toggle-pool', heroId: hero.id }, false)} aria-label={`${inPool ? '移出' : '加入'}个人英雄池`}><Heart size={13} fill={inPool ? 'currentColor' : 'none'} aria-hidden="true" /></button>
              </div>;
            })}
            {!filteredHeroes.length && <p className="empty-result">当前筛选下没有英雄</p>}
          </div>
        </section>

        <aside className="recommendation-panel">
          <div className="recommendation-header">
            <div><span className="eyebrow">MODEL OUTPUT</span><h2>{recommendationKind === 'pick' ? '选人建议' : '禁用建议'}</h2></div>
            <div className="rec-switch">
              <button className={recommendationKind === 'pick' ? 'is-active' : ''} aria-pressed={recommendationKind === 'pick'} onClick={() => setRecommendationKind('pick')}>选人</button>
              {state.mode === 'league' && <button className={recommendationKind === 'ban' ? 'is-active' : ''} aria-pressed={recommendationKind === 'ban'} onClick={() => setRecommendationKind('ban')}>禁用</button>}
            </div>
          </div>
          <div className="formula-note">{recommendationKind === 'pick' ? '对线 35 · META 20 · 克制 15 · 协同 15 · 位置 15' : '对线威胁 35 · LEAGUE META 30 · 整体克制 25 · 灵活性 10'}</div>
          <div className="recommendation-list">
            {recommendations.map((item, index) => <RecommendationCard key={item.hero.id} item={item} rank={index + 1} relationshipSource={relationshipSource} priority={index === 0} onPick={() => apply({ type: 'add', heroId: item.hero.id, zone: recommendationKind === 'ban' ? 'ban' : 'ally' })} />)}
            {!recommendations.length && <div className="empty-recommendation"><p>当前条件下没有候选</p><small>调整位置或个人英雄池范围</small></div>}
          </div>
        </aside>
      </main>

      <footer className="data-footer">
        <span>当前游戏补丁 {snapshot.metadata.patch} · 数据版本 {snapshot.metadata.version} · 更新 {generatedAt}</span>
        <span className={`footer-dataset is-${activeDataset.status}`}>{state.mode === 'ranked' ? '野排' : '联赛'}：{datasetPatch} · {datasetStatus} · {activeDataset.source}</span>
        <span>数据仅供辅助判断 · <span translate="no">Dota 2</span> 与相关商标归 <span translate="no">Valve Corporation</span> 所有。本项目与 <span translate="no">Valve</span> 无关联。</span>
      </footer>
    </div>
  );
}
